import { useMemo } from "react"
import styled from "styled-components"

import { MotionIcon } from "@minuthq/meatball-ui-icons"
import { Skeleton, spacing, Text } from "@minuthq/meatball-ui-react"
import { isFuture, isSameDay } from "date-fns"

import {
  useFetchDevices,
  useFetchMultipleDeviceMotionReadings,
} from "src/data/devices/queries/deviceQueries"
import { IDeviceMotionReadings } from "src/data/devices/types/deviceTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { TTask } from "src/data/taskManagement/taskManagementTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { TClockType } from "src/data/user/user"
import { useTranslate } from "src/i18n/useTranslate"
import { getMotionResolution } from "src/ui/Graphs/utils"
import { LocalizedDate } from "src/ui/LocalizedDate/LocalizedDate"
import { capitalizeFirstChar, formatDateAsTime } from "src/utils/l10n"
import { ZonedDateTime } from "src/utils/ZonedDateTime"

export function TaskMotion({
  task,
  timezone,
}: {
  task: TTask
  timezone?: string
}) {
  const { t, langKeys } = useTranslate()
  const { orgId } = useOrganization()
  const user = useGetUser()
  const clockType = user.clock_type

  const fetchDevices = useFetchDevices({
    orgId,
    filter: { home_ids: [task.home_id ?? ""] },
    options: { enabled: !!task.home_id },
  })

  const deviceIds = useMemo(
    () => fetchDevices.data?.devices.map((device) => device.device_id) ?? [],
    [fetchDevices.data]
  )

  const startDate = useMemo(
    () =>
      task.time_window.earliest_start_date
        ? new Date(task.time_window.earliest_start_date)
        : timezone
          ? new ZonedDateTime(new Date(task.time_window.due_date), timezone)
              .atStartOfDay()
              .asDate()
          : new Date(task.time_window.due_date),
    [task.time_window.earliest_start_date, task.time_window.due_date, timezone]
  )

  const endDate = useMemo(
    () => new Date(task.time_window.due_date),
    [task.time_window.due_date]
  )

  const isSameDayTask = isSameDay(startDate, endDate)

  const motionBucketSize = useMemo(
    () => getMotionResolution({ startDate, endDate }),
    [startDate, endDate]
  )

  const fetchMultipleDeviceMotionReadings =
    useFetchMultipleDeviceMotionReadings({
      orgId,
      deviceIds,
      endAt: endDate.toISOString(),
      startAt: startDate.toISOString(),
      timeResolution: motionBucketSize,
      options: {
        enabled: !!task.home_id && deviceIds.length > 0,
      },
    })

  const { firstMotion, lastMotion, isMotionLoading } = useMemo(() => {
    const loading = fetchMultipleDeviceMotionReadings.isInitialLoading
    const error = fetchMultipleDeviceMotionReadings.isError
    if (loading || error) {
      return {
        firstMotion: undefined,
        lastMotion: undefined,
        isMotionLoading: error ? false : loading,
      }
    }

    const motionReadings = fetchMultipleDeviceMotionReadings.data ?? []
    const { first, last } = getFirstAndLastMotionValues(motionReadings)

    return {
      firstMotion: first,
      lastMotion: last,
      isMotionLoading: false,
    }
  }, [
    fetchMultipleDeviceMotionReadings.isInitialLoading,
    fetchMultipleDeviceMotionReadings.isError,
    fetchMultipleDeviceMotionReadings.data,
  ])

  return (
    <Box>
      <MotionTitle>
        <MotionIcon width={22} />
        <Text variant="subtitle">{t(langKeys.motion)}</Text>
      </MotionTitle>

      {!firstMotion &&
      !lastMotion &&
      !fetchDevices.isInitialLoading &&
      !isMotionLoading ? (
        <Text variant="bodyS">
          {isFuture(new Date(task.time_window.due_date))
            ? t(langKeys.task_management_motion_check_after_schedule_text)
            : t(
                langKeys.task_management_no_motion_detected_during_schedule_text
              )}
        </Text>
      ) : (
        <TimeBox>
          <Text variant="bodyS">
            {capitalizeFirstChar(t(langKeys.first))}:{" "}
            <MotionDate
              date={firstMotion}
              timezone={timezone}
              loading={fetchDevices.isInitialLoading || isMotionLoading}
              clockType={clockType}
              showTimeOnly={isSameDayTask}
            />
          </Text>

          <Text variant="bodyS">
            {t(langKeys.last)}:{" "}
            <MotionDate
              date={lastMotion}
              timezone={timezone}
              loading={fetchDevices.isInitialLoading || isMotionLoading}
              clockType={clockType}
              showTimeOnly={isSameDayTask}
            />
          </Text>
        </TimeBox>
      )}
    </Box>
  )
}

function MotionDate({
  date,
  timezone,
  loading,
  clockType,
  showTimeOnly,
}: {
  date: number | undefined
  timezone?: string
  loading: boolean
  clockType?: TClockType
  showTimeOnly?: boolean
}) {
  const { t, langKeys } = useTranslate()

  if (loading) {
    return (
      <InlineSkeleton>
        <Skeleton width={70} height={30} />
      </InlineSkeleton>
    )
  }

  if (!date) {
    return <span>{t(langKeys.none).toLowerCase()}</span>
  }

  if (showTimeOnly) {
    return formatDateAsTime({
      date: new Date(date * 1000).toISOString(),
      clockType,
      timezone,
      excludeSeconds: true,
    })
  }

  return (
    <LocalizedDate
      date={new Date(date * 1000).toISOString()}
      timezone={timezone}
    />
  )
}

function getFirstAndLastMotionValues(motionReadings: IDeviceMotionReadings[]) {
  if (!motionReadings?.length) {
    return { first: undefined, last: undefined }
  }

  const timestamps = motionReadings
    .flatMap((device) => device?.values || [])
    .filter(([timestamp, motion]) => timestamp && motion > 0)
    .map(([timestamp]) => timestamp)
    .sort((a, b) => a - b)

  return timestamps.length
    ? { first: timestamps[0], last: timestamps.at(-1) }
    : { first: undefined, last: undefined }
}

const Box = styled.div`
  display: grid;
  gap: ${spacing.XS2};
`

const TimeBox = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.XS};
`

const MotionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`

const InlineSkeleton = styled.span`
  display: inline-block;
  vertical-align: middle;
`
